import { AxiosRequestConfig, AxiosResponse } from 'axios'

import { Api } from 'common/api/api'
import { getDownloadFileName } from 'common/helpers/downloadFileName'
import { apiConfig } from 'common/api/api.config'

import {
  ExportComponentResponse,
  InterfaceIndexValuesData,
  RowLastUpdateInformation
} from 'common/types/interface'
import { GetFormColumnsData, IndexFilterChoices, IndexTextFilterChoices } from 'common/types/model'
import { ws } from 'store/socket/sagas'
import { ComponentCode, GridLayout, InterfaceCode } from 'pages/interfaces/containers/types'
import {
  FormChange,
  FormRowInsertMode,
  HistoryFileItem,
  IFilterComponent,
  InputNodeHistoryBatchesPage,
  InputNodeHistoryBatchesRequest,
  InputNodeHistoryExport,
  InputNodeHistoryExportRequest,
  InputNodeHistoryListPage,
  InputNodeHistoryPage,
  InputNodeHistoryRequest,
  InputNodeHistoryUser,
  InputNodeHistoryUserSummary,
  TableChange,
  TableChangePageData,
  UploadFileFlowResponse,
  UploadManagerItem
} from 'pages/interfaces/components/types'
import { GenericBooleanResponse } from 'common/types/managers'

const ENDPOINT = '/interfaces'
const WS_TIME_INTERVAL_MS = 100

class InterfaceApi extends Api {
  public constructor(config: AxiosRequestConfig) {
    super(config)

    // this middleware is been called right before the http request is made.
    this.interceptors.request.use((param: AxiosRequestConfig) => ({
      ...param
    }))

    // this middleware is been called right before the response is get it by the method that triggers the request
    this.interceptors.response.use((param: AxiosResponse) => ({
      ...param
    }))

    Object.setPrototypeOf(this, InterfaceApi.prototype)
    this.getInterfaceIndexValues = this.getInterfaceIndexValues.bind(this)
    this.getInterfaceTableColumns = this.getInterfaceTableColumns.bind(this)
    this.getFormSelectorColumnValues = this.getFormSelectorColumnValues.bind(this)
    this.getInterfaceFormColumns = this.getInterfaceFormColumns.bind(this)
    this.silentApplyFormChanges = this.silentApplyFormChanges.bind(this)
    this.applyFormChanges = this.applyFormChanges.bind(this)
    this.silentApplyTableChanges = this.silentApplyTableChanges.bind(this)
    this.applyTableChanges = this.applyTableChanges.bind(this)
    this.validateFormValues = this.validateFormValues.bind(this)
    this.addRowsToForm = this.addRowsToForm.bind(this)
    this.removeRowsFromForm = this.removeRowsFromForm.bind(this)
    this.getFormRowLastUpdateInfo = this.getFormRowLastUpdateInfo.bind(this)
    this.getInputNodeHistory = this.getInputNodeHistory.bind(this)
    this.getInputNodeHistoryBatches = this.getInputNodeHistoryBatches.bind(this)
    this.getInputNodeHistoryUsers = this.getInputNodeHistoryUsers.bind(this)
    this.getInputNodeHistoryByUser = this.getInputNodeHistoryByUser.bind(this)
    this.exportInputNodeHistory = this.exportInputNodeHistory.bind(this)
    this.exportComponent = this.exportComponent.bind(this)
    this.trigger = this.trigger.bind(this)
    this.getLayout = this.getLayout.bind(this)
    this.selectInterfaceCustomView = this.selectInterfaceCustomView.bind(this)
    this.createInterfaceCustomView = this.createInterfaceCustomView.bind(this)
    this.saveInterfaceCustomView = this.saveInterfaceCustomView.bind(this)
    this.updateInterfaceCustomView = this.updateInterfaceCustomView.bind(this)
    this.deleteInterfaceCustomView = this.deleteInterfaceCustomView.bind(this)
    this.applyComponentUndo = this.applyComponentUndo.bind(this)
    this.applyComponentRedo = this.applyComponentRedo.bind(this)
  }

  public async getLayout(
    interfaceId: string,
    recreateInterfaceNode: boolean
  ): Promise<GridLayout | undefined> {
    try {
      const res: AxiosResponse<GridLayout | undefined> = await this.post(
        `${ENDPOINT}/${interfaceId}/layout/`,
        {
          recreate_interface_node: recreateInterfaceNode
        }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  /** Persist python_code and/or html for an HTML Interface (UI editor). */
  public async updateHtmlCode(
    interfaceId: string,
    payload: { python_code?: string; html?: string }
  ): Promise<{
    id?: string
    updated_sections?: string[]
    ok?: boolean
    callbacks?: Array<{ name: string; signature: string; doc: string }>
    error?: string
  }> {
    const res: AxiosResponse<any> = await this.post(`${ENDPOINT}/${interfaceId}/htmlCode/`, payload)
    return this.success(res)
  }

  async _trigger(interfaceId: string, triggerData: any, fromInterface: boolean): Promise<boolean> {
    const res: AxiosResponse<boolean> = await this.post(`${ENDPOINT}/${interfaceId}/trigger/`, {
      triggerData,
      fromInterface
    })
    return this.success(res)
  }

  public async trigger(
    interfaceId: string,
    triggerData: any,
    fromInterface: boolean
  ): Promise<boolean> {
    try {
      // Try _trigger the first time to avoid delay from setInterval
      if (ws?.readyState === 1) {
        return await this._trigger(interfaceId, triggerData, fromInterface)
      }
      const triggerCall = setInterval(async () => {
        if (ws?.readyState === 1) {
          clearInterval(triggerCall)
          return await this._trigger(interfaceId, triggerData, fromInterface)
        }
      }, WS_TIME_INTERVAL_MS)
      return false
    } catch (error) {
      throw error
    }
  }

  public async getInterfaceCode(interfaceId: string): Promise<InterfaceCode> {
    try {
      const res: AxiosResponse<InterfaceCode> = await this.get(
        `${ENDPOINT}/${interfaceId}/getcode/`
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async setInterfaceCode(interfaceId: string, code: string): Promise<void> {
    try {
      const res: AxiosResponse<InterfaceCode> = await this.post(
        `${ENDPOINT}/${interfaceId}/setcode/`,
        { code },
        {
          headers: { 'Content-Type': 'application/json' }
        }
      )
      this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async saveInterfaceCode(interfaceId: string): Promise<GridLayout> {
    try {
      const res: AxiosResponse<GridLayout> = await this.post(`${ENDPOINT}/${interfaceId}/saveCode/`)
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async getComponentCode(interfaceId: string, componentId: string): Promise<ComponentCode> {
    try {
      const res: AxiosResponse<ComponentCode> = await this.get(
        `${ENDPOINT}/${interfaceId}/getComponentCode/${componentId}/`
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async getInterfaceIndexValues(
    interfaceId: string,
    componentId: string,
    indexId: string,
    paginateResults: boolean,
    page?: number,
    filter?: IndexFilterChoices | null,
    text1?: string | null,
    page_size?: number,
    sort?: string | null,
    applyFilters: boolean = true
  ): Promise<InterfaceIndexValuesData> {
    try {
      let data = {
        interface_id: interfaceId,
        component_id: componentId,
        index_id: indexId,
        paginate_results: paginateResults,
        apply_filters: applyFilters,
        page_size,
        sort
      }
      if (page) {
        data = { ...data, ...{ page } }
      }
      if (filter && text1) {
        data = { ...data, ...{ filter, text1 } }
      }
      const res: AxiosResponse<InterfaceIndexValuesData> = await this.post(
        `${ENDPOINT}/${interfaceId}/getInterfaceIndexValues/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async getInterfaceTableColumns(
    interfaceId: string,
    componentId: string,
    indexId: string,
    paginateResults: boolean,
    page?: number,
    filter?: IndexFilterChoices | null,
    text1?: string | null,
    applyFilters: boolean = true
  ): Promise<InterfaceIndexValuesData> {
    try {
      let data = {
        interface_id: interfaceId,
        component_id: componentId,
        paginate_results: paginateResults
      }
      if (page) {
        data = { ...data, ...{ page } }
      }
      if (filter && text1) {
        data = { ...data, ...{ filter, text1 } }
      }
      const res: AxiosResponse<InterfaceIndexValuesData> = await this.post(
        `${ENDPOINT}/${interfaceId}/getInterfaceTableColumns/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async getFormSelectorColumnValues(
    interfaceId: string,
    componentId: string,
    rowId: number,
    columnName: string,
    text?: string,
    offset?: number
  ): Promise<(string | number)[]> {
    try {
      let data = {
        interface_id: interfaceId,
        component_id: componentId,
        row_id: rowId,
        column_name: columnName
      }
      if (text) {
        data = { ...data, ...{ text } }
      }
      if (offset) {
        data = { ...data, ...{ offset } }
      }
      const res: AxiosResponse<(string | number)[]> = await this.post(
        `${ENDPOINT}/${interfaceId}/getFormSelectorColumnValues/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async getInterfaceFormColumns(
    interfaceId: string,
    componentId: string,
    paginateResults: boolean,
    page?: number,
    filter?: IndexTextFilterChoices | null,
    text1?: string | null,
    page_size?: number,
    sort?: string | null
  ): Promise<GetFormColumnsData> {
    try {
      let data = {
        interface_id: interfaceId,
        component_id: componentId,
        paginate_results: paginateResults,
        page_size
      }
      if (page) {
        data = { ...data, ...{ page } }
      }
      if (filter && text1) {
        data = { ...data, ...{ filter, text1 } }
      }
      if (sort) {
        data = { ...data, ...{ sort } }
      }
      const res: AxiosResponse<GetFormColumnsData> = await this.post(
        `${ENDPOINT}/${interfaceId}/getInterfaceFormColumns/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async validateFormValues(
    interfaceId: string,
    componentId: string,
    newValues: any[],
    text?: string,
    rowOverlays?: Record<string, Record<string, any>>,
    selectorDomainsOnly?: boolean
  ): Promise<boolean[]> {
    try {
      let data = {
        interface_id: interfaceId,
        component_id: componentId,
        new_values: newValues
      }
      if (text) {
        data = { ...data, ...{ text } }
      }
      if (rowOverlays) {
        data = { ...data, ...{ row_overlays: rowOverlays } }
      }
      if (selectorDomainsOnly) {
        data = { ...data, ...{ selector_domains_only: true } }
      }
      const res: AxiosResponse<boolean[]> = await this.post(
        `${ENDPOINT}/${interfaceId}/validateFormValues/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async silentApplyFormChanges(
    interfaceId: string,
    componentId: string,
    changes: FormChange[]
  ): Promise<GenericBooleanResponse> {
    try {
      const data = {
        interface_id: interfaceId,
        component_id: componentId,
        changes
      }
      const res: AxiosResponse<GenericBooleanResponse> = await this.post(
        `${ENDPOINT}/${interfaceId}/silentApplyFormChanges/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async applyFormChanges(
    interfaceId: string,
    componentId: string,
    changes: FormChange[]
  ): Promise<Array<{ string: any }>> {
    try {
      const data = {
        interface_id: interfaceId,
        component_id: componentId,
        changes
      }
      const res: AxiosResponse<Array<{ string: any }>> = await this.post(
        `${ENDPOINT}/${interfaceId}/applyFormChanges/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async silentApplyTableChanges(
    interfaceId: string,
    componentId: string,
    changes: TableChange[],
    pageData: TableChangePageData
  ): Promise<GenericBooleanResponse> {
    try {
      const data = {
        interface_id: interfaceId,
        component_id: componentId,
        changes,
        page_data: pageData
      }
      const res: AxiosResponse<GenericBooleanResponse> = await this.post(
        `${ENDPOINT}/${interfaceId}/silentApplyTableChanges/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async applyTableChanges(
    interfaceId: string,
    componentId: string,
    changes: TableChange[],
    pageData: TableChangePageData
  ): Promise<Array<{ string: any }>> {
    try {
      const data = {
        interface_id: interfaceId,
        component_id: componentId,
        changes,
        page_data: pageData
      }
      const res: AxiosResponse<Array<{ string: any }>> = await this.post(
        `${ENDPOINT}/${interfaceId}/applyTableChanges/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async addRowsToForm(
    interfaceId: string,
    componentId: string,
    numberOfRows: number,
    insertMode?: FormRowInsertMode,
    anchorRowId?: number | string
  ): Promise<Array<{ string: any }>> {
    try {
      const data = {
        interface_id: interfaceId,
        component_id: componentId,
        number_of_rows: numberOfRows,
        insert_mode: insertMode,
        anchor_row_id: anchorRowId
      }
      const res: AxiosResponse<Array<{ string: any }>> = await this.post(
        `${ENDPOINT}/${interfaceId}/addRowsToForm/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async removeRowsFromForm(
    interfaceId: string,
    componentId: string,
    rowIdsToRemove: number[]
  ): Promise<Array<{ string: any }>> {
    try {
      const data = {
        interface_id: interfaceId,
        component_id: componentId,
        row_ids_to_remove: rowIdsToRemove
      }
      const res: AxiosResponse<Array<{ string: any }>> = await this.post(
        `${ENDPOINT}/${interfaceId}/removeRowsFromForm/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async refreshChanges(interfaceId: string, fullRefresh?: boolean): Promise<void> {
    try {
      const res: AxiosResponse<GridLayout> = await this.post(
        `${ENDPOINT}/${interfaceId}/refreshInterfaceChanges/`,
        { full_refresh: fullRefresh || false }
      )
      this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async getFormRowLastUpdateInfo(
    formNodeId: string,
    rowId: number | string
  ): Promise<RowLastUpdateInformation> {
    try {
      const data = {
        form_node_id: formNodeId,
        row_id: rowId
      }
      const res: AxiosResponse<RowLastUpdateInformation> = await this.post(
        `${ENDPOINT}/getFormRowLastUpdateInfo/`,
        data
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async getInputNodeHistory(
    request: InputNodeHistoryRequest
  ): Promise<InputNodeHistoryPage> {
    const res: AxiosResponse<InputNodeHistoryPage> = await this.post(
      `${ENDPOINT}/getInputNodeHistory/`,
      request
    )
    return this.success(res)
  }

  public async getInputNodeHistoryBatches(
    request: InputNodeHistoryBatchesRequest
  ): Promise<InputNodeHistoryBatchesPage> {
    const res: AxiosResponse<InputNodeHistoryBatchesPage> = await this.post(
      `${ENDPOINT}/getInputNodeHistoryBatches/`,
      request
    )
    return this.success(res)
  }

  public async getInputNodeHistoryByUser(
    request: InputNodeHistoryBatchesRequest
  ): Promise<InputNodeHistoryListPage<InputNodeHistoryUserSummary>> {
    const res: AxiosResponse<
      InputNodeHistoryListPage<InputNodeHistoryUserSummary>
    > = await this.post(`${ENDPOINT}/getInputNodeHistoryByUser/`, request)
    return this.success(res)
  }

  public async getInputNodeHistoryUsers(nodeId: string): Promise<InputNodeHistoryUser[]> {
    const res: AxiosResponse<
      InputNodeHistoryUser[]
    > = await this.post(`${ENDPOINT}/getInputNodeHistoryUsers/`, { node_id: nodeId })
    return this.success(res)
  }

  public async exportInputNodeHistory(
    request: InputNodeHistoryExportRequest
  ): Promise<InputNodeHistoryExport> {
    const res: AxiosResponse<Blob> = await this.post(
      `${ENDPOINT}/exportInputNodeHistory/`,
      request,
      {
        responseType: 'blob'
      }
    )
    return { data: this.success(res), fileName: getDownloadFileName(res.headers) }
  }

  public async exportComponent(data: any): Promise<ExportComponentResponse<string>> {
    try {
      const res: AxiosResponse<ExportComponentResponse<string>> = await this.post(
        `${ENDPOINT}/exportComponent/`,
        data,
        {
          responseType: 'blob'
        }
      )
      return { data: this.success<any>(res), headers: res.headers }
    } catch (error) {
      throw error
    }
  }

  public async exportHtmlView(data: any): Promise<ExportComponentResponse<string>> {
    try {
      const res: AxiosResponse<ExportComponentResponse<string>> = await this.post(
        `${ENDPOINT}/exportHtmlView/`,
        data,
        {
          responseType: 'blob'
        }
      )
      return { data: this.success<any>(res), headers: res.headers }
    } catch (error) {
      throw error
    }
  }

  /** The Excel add-in's workbook, linked to a table of an HTML interface. */
  public async htmlTableWorkbook(data: Record<string, unknown>): Promise<AxiosResponse<Blob>> {
    return this.post('/officeaddin/htmlTableWorkbook/', data, { responseType: 'blob' })
  }

  public async selectInterfaceCustomView(
    interfaceId: string,
    viewId: string | null
  ): Promise<GenericBooleanResponse> {
    try {
      const res: AxiosResponse<GenericBooleanResponse> = await this.post(
        `${ENDPOINT}/${interfaceId}/selectInterfaceCustomView/`,
        { view_id: viewId }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async createInterfaceCustomView(
    interfaceId: string,
    name: string,
    isDefault: boolean,
    state?: Record<string, unknown>
  ): Promise<GenericBooleanResponse> {
    try {
      const res: AxiosResponse<GenericBooleanResponse> = await this.post(
        `${ENDPOINT}/${interfaceId}/createInterfaceCustomView/`,
        { name, is_default: isDefault, state }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async saveInterfaceCustomView(
    interfaceId: string,
    viewId: string,
    state?: Record<string, unknown>
  ): Promise<GenericBooleanResponse> {
    try {
      const res: AxiosResponse<GenericBooleanResponse> = await this.post(
        `${ENDPOINT}/${interfaceId}/saveInterfaceCustomView/`,
        { view_id: viewId, state }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async updateInterfaceCustomView(
    interfaceId: string,
    viewId: string,
    { name, isDefault }: { name?: string; isDefault?: boolean }
  ): Promise<GenericBooleanResponse> {
    try {
      const res: AxiosResponse<GenericBooleanResponse> = await this.post(
        `${ENDPOINT}/${interfaceId}/updateInterfaceCustomView/`,
        { view_id: viewId, name, is_default: isDefault }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async deleteInterfaceCustomView(
    interfaceId: string,
    viewId: string
  ): Promise<GenericBooleanResponse> {
    try {
      const res: AxiosResponse<GenericBooleanResponse> = await this.post(
        `${ENDPOINT}/${interfaceId}/deleteInterfaceCustomView/`,
        { view_id: viewId }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async addFiltersToFilterComponent(
    nodes: string[],
    componentId: string,
    interfaceId: string
  ): Promise<void> {
    try {
      const res: AxiosResponse<void> = await this.post(`${ENDPOINT}/addFiltersToFilterComponent/`, {
        nodes,
        componentId,
        interfaceId
      })
      this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async removeFiltersFromFilterComponent(
    componentToRemoveId: string,
    componentId: string,
    interfaceId: string
  ): Promise<void> {
    try {
      const res: AxiosResponse<void> = await this.post(
        `${ENDPOINT}/removeFiltersFromFilterComponent/`,
        {
          componentId,
          componentToRemoveId,
          interfaceId
        }
      )
      this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async saveFilters(
    componentId: string,
    interfaceId: string,
    order: string[]
  ): Promise<void> {
    try {
      const res: AxiosResponse<void> = await this.post(`${ENDPOINT}/saveFilters/`, {
        componentId,
        interfaceId,
        order
      })
      this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async revertFilters(component: IFilterComponent, interfaceId: string): Promise<void> {
    try {
      const res: AxiosResponse<void> = await this.post(`${ENDPOINT}/revertFilters/`, {
        component,
        interfaceId
      })
      this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async applyComponentUndo(
    interfaceId: string,
    componentId: string,
    fromInterface: boolean
  ): Promise<void> {
    try {
      const res: AxiosResponse<void> = await this.post(`${ENDPOINT}/applyComponentUndo/`, {
        interfaceId,
        componentId,
        fromInterface
      })
      this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async applyComponentRedo(
    interfaceId: string,
    componentId: string,
    fromInterface: boolean
  ): Promise<void> {
    try {
      const res: AxiosResponse<void> = await this.post(`${ENDPOINT}/applyComponentRedo/`, {
        interfaceId,
        componentId,
        fromInterface
      })
      this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async uploadFileFlow(
    uploadManagerItem: UploadManagerItem,
    tmpPath: string,
    interfaceId: string,
    componentId: string
  ): Promise<UploadFileFlowResponse> {
    try {
      const res: AxiosResponse<UploadFileFlowResponse> = await this.post(
        `${ENDPOINT}/uploadFileFlow/`,
        { uploadManagerItem, tmpPath, interfaceId, componentId }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async getHistoryFiles(
    itemId: string,
    interfaceId: string,
    componentId: string,
    orderBy: string
  ): Promise<HistoryFileItem[]> {
    try {
      const query = {
        itemId,
        interfaceId,
        componentId,
        orderBy
      }
      const res: AxiosResponse<HistoryFileItem[]> = await this.get(`${ENDPOINT}/getHistoryFiles/`, {
        params: query
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async restoreOldFile(
    completePath: string,
    interfaceId: string,
    componentId: string,
    itemId: string
  ): Promise<UploadFileFlowResponse> {
    try {
      const res: AxiosResponse<UploadFileFlowResponse> = await this.post(
        `${ENDPOINT}/restoreOldFile/`,
        { completePath, interfaceId, componentId, itemId }
      )
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async removeOldFile(
    completePath: string,
    interfaceId: string,
    componentId: string,
    itemId: string
  ): Promise<boolean> {
    try {
      const res: AxiosResponse<boolean> = await this.post(`${ENDPOINT}/removeOldFile/`, {
        completePath,
        interfaceId,
        componentId,
        itemId
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
  public async getItemFolderFiles(
    folderPath: string,
    interfaceId: string,
    componentId: string,
    itemId: string
  ): Promise<any[]> {
    try {
      const query = {
        folderPath,
        interfaceId,
        componentId,
        itemId
      }
      const res: AxiosResponse<any[]> = await this.get(`${ENDPOINT}/getItemFolderFiles/`, {
        params: query
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }

  public async removeFileFromFolder(
    completePath: string,
    interfaceId: string,
    componentId: string,
    itemId: string
  ): Promise<boolean> {
    try {
      const res: AxiosResponse<boolean> = await this.post(`${ENDPOINT}/removeFileFromFolder/`, {
        completePath,
        interfaceId,
        componentId,
        itemId
      })
      return this.success(res)
    } catch (error) {
      throw error
    }
  }
}

export const interfaceApi = new InterfaceApi(apiConfig)
